/**
 * Commission Injection/Extraction Helpers
 *
 * Manages temporary injection of commission data from RFP-level broker.commissions[]
 * into individual benefit plan benefits objects for UI display.
 *
 * Flow:
 * 1. On Load: broker.commissions[] → inject into benefitPlan.benefits
 * 2. In UI: Commission fields edited like any other benefit field
 * 3. On Save: Extract from benefitPlan.benefits → PATCH to commission endpoint
 */

import { RfpCoverage } from "./types";
import {
  extractCommissionsForCoverage,
  upsertCommissionsForCoverage,
  ApiCommission,
} from "./commissionHelpers";

export const commissionFields = [
  "currentCommissionType",
  "currentCommissionAmount",
  "requestedCommissionType",
  "requestedCommissionAmount",
];

/**
 * Injects commission data from broker.commissions[] into each benefit plan's benefits object
 *
 * This is called after loading RFP data from the API, before displaying in the grid.
 * Commission fields are added to each benefit plan's benefits object so they can be
 * edited like any other field in the UI.
 *
 * @param coverage - The coverage with benefit plans to inject commissions into
 * @param brokerCommissions - The RFP-level broker.commissions[] array
 * @returns Updated coverage with commission fields injected into each benefit plan
 *
 * @example
 * ```ts
 * const rfp = await loadRfp(rfpId);
 * const coverage = rfp.coverages.find(c => c.type === 'dental');
 * const updatedCoverage = injectCommissionsIntoCoverage(coverage, rfp.broker?.commissions);
 * ```
 */
export const injectCommissionsIntoCoverage = (
  coverage: RfpCoverage,
  brokerCommissions?: ApiCommission[],
): RfpCoverage => {
  if (!coverage.benefitPlans || coverage.benefitPlans.length === 0) {
    return coverage;
  }

  if (!brokerCommissions || brokerCommissions.length === 0) {
    return coverage;
  }

  const updatedCoverage: RfpCoverage = {
    ...coverage,
    benefitPlans: coverage.benefitPlans.map((plan) => {
      const planId = plan.id || plan.identifier;

      if (!planId) {
        return plan;
      }

      const commissionData = extractCommissionsForCoverage(
        brokerCommissions,
        coverage.coverageType,
        planId,
      );

      if (Object.keys(commissionData).length === 0) {
        return plan;
      }

      return {
        ...plan,
        benefits: {
          ...plan.benefits,
          ...commissionData,
        },
      };
    }),
  };

  return updatedCoverage;
};

/**
 * Extracts commission data from benefit plans and updates broker.commissions[] array
 *
 * This is called before saving RFP data, after user edits in the grid.
 * Commission fields are extracted from each benefit plan's benefits object and
 * aggregated back into the RFP-level broker.commissions[] array.
 *
 * @param coverage - The coverage with benefit plans containing commission fields
 * @param brokerCommissions - The current RFP-level broker.commissions[] array
 * @returns Updated broker.commissions[] array with new commission data
 *
 * @example
 * ```ts
 * const updatedCommissions = extractCommissionsFromCoverage(
 *   coverage,
 *   rfp.broker?.commissions || []
 * );
 * rfp.broker.commissions = updatedCommissions;
 * await saveRfp(rfp);
 * ```
 */
export const extractCommissionsFromCoverage = (
  coverage: RfpCoverage,
  brokerCommissions?: ApiCommission[],
): ApiCommission[] => {
  if (!coverage.benefitPlans || coverage.benefitPlans.length === 0) {
    return brokerCommissions || [];
  }

  let updatedCommissions = brokerCommissions || [];

  for (const plan of coverage.benefitPlans) {
    if (!plan.identifier) {
      continue;
    }

    const benefits = plan.benefits || {};
    const currentType = benefits.currentCommissionType;
    const currentAmount = benefits.currentCommissionAmount;
    const requestedType = benefits.requestedCommissionType;
    const requestedAmount = benefits.requestedCommissionAmount;

    updatedCommissions = upsertCommissionsForCoverage(
      updatedCommissions,
      coverage.type,
      plan.identifier,
      currentType,
      currentAmount,
      requestedType,
      requestedAmount,
    );
  }

  return updatedCommissions;
};

/**
 * Extracts commission data from all coverages and updates broker.commissions[] array
 *
 * This is a convenience function that processes multiple coverages at once.
 * Useful when saving the entire RFP with multiple coverages.
 *
 * @param coverages - Array of coverages to extract commissions from
 * @param brokerCommissions - The current RFP-level broker.commissions[] array
 * @returns Updated broker.commissions[] array with commission data from all coverages
 *
 * @example
 * ```ts
 * const updatedCommissions = extractCommissionsFromAllCoverages(
 *   rfp.coverages,
 *   rfp.broker?.commissions || []
 * );
 * rfp.broker.commissions = updatedCommissions;
 * await saveRfp(rfp);
 * ```
 */
export const extractCommissionsFromAllCoverages = (
  coverages: RfpCoverage[],
  brokerCommissions?: ApiCommission[],
): ApiCommission[] => {
  let updatedCommissions = brokerCommissions || [];

  for (const coverage of coverages) {
    updatedCommissions = extractCommissionsFromCoverage(
      coverage,
      updatedCommissions,
    );
  }

  return updatedCommissions;
};
